<template>
  <div class="product-gallery">
    <splide
      v-if="items.length > 1"
      ref="splide"
      :options="options"
      :has-track="false"
      @splide:active="handleActiveEvent"
      @splide:mounted="handleSplideInit"
    >
      <button
        type="button"
        class="product-gallery__button"
        :aria-label="$string('accessibility.carousel.previous')"
        @click="$refs.splide.go('<')"
      >
        <div class="product-gallery__button-icon">
          <icon-chevron-left />
        </div>
      </button>

      <splide-track class="product-gallery__track">
        <splide-slide
          v-for="(item, index) of items"
          :key="`gallery-item-${index}`"
          class="product-gallery__slide"
        >
          <responsive-image
            class="product-gallery__slide-image"
            js-product-gallery="image"
            :image="item"
            :ratio="responsiveImageRatio"
            min-max="640-2048"
            loaded-animation="false"
            :lazyload="false"
            sizes="(min-width: 1024px) 50vw, 100vw"
            @image-loaded="() => handleGalleryLoading(index)"
          />
        </splide-slide>
      </splide-track>

      <button
        class="product-gallery__button product-gallery__button--next"
        type="button"
        :aria-label="$string('accessibility.carousel.next')"
        @click="$refs.splide.go('>')"
      >
        <div class="product-gallery__button-icon">
          <icon-chevron-right />
        </div>
      </button>

      <div
        class="
          product-gallery__counter
          text-body-xs-label
          text-body-s-label-desktop
        "
      >
        <span
          class="product-gallery__count"
          v-text="counter.active"
        />

        <span v-text="counter.total" />
      </div>
    </splide>

    <responsive-image
      v-else
      :image="items[0]"
      :ratio="responsiveImageRatio"
      @image-loaded="() => handleGalleryLoading(0)"
    />
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Default product gallery layout with simple controls.
 * @param {Array} items - The list of gallery items.
 *
 */
import { mapState } from 'vuex'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'
import ResponsiveImage from '~global/images/responsive-image'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ProductGallery',

  components: {
    IconChevronLeft,
    IconChevronRight,
    Splide,
    SplideSlide,
    SplideTrack,
    ResponsiveImage,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    const ratio = {
      default: {
        height: 468.75,
        width: 375,
      },
      desktop: {
        height: 927,
        width: 927,
      },
    }

    return {
      active: 1,
      ratio,
      options: {
        autoHeight: true,
        type: 'loop',
        arrows: false,
        pagination: false,
        lazyLoad: 'nearby',
        preloadPages: 3,
        heightRatio: ratio.default.height / ratio.default.width,
        mediaQuery: 'min',
        speed: 400,
        drag: 'free',
        snap: true,
        flickMaxPages: 1,
        flickPower: 100,
        waitForTransition: false,
        breakpoints: {
          [breakpoints.l]: {
            heightRatio: ratio.desktop.height / ratio.desktop.width,
            speed: 0,
          },
        },
      },
    }
  },

  computed: {

    /**
     * Maps the Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns the counter values.
     * @returns {Object}
     */
    counter() {
      return {
        active: String(this.active).padStart(2, 0),
        total: String(this.items.length).padStart(2, 0),
      }
    },

    /**
     * Returns the ratio for the Responsive Image component.
     * @returns {Number}
     */
    responsiveImageRatio() {
      if (this.isMobile) {
        return this.ratio.default.width / this.ratio.default.height
      }

      return this.ratio.desktop.width / this.ratio.desktop.height
    },
  },

  watch: {
    isMobile: {
      immediate: true,
      handler(value) {
        if (value) {
          return
        }

        this.options.speed = 0
      },
    },
  },

  methods: {

    /**
     * Handles actions to be run when the gallery initialises.
     * @param {Object} splide
     */
    handleSplideInit(splide) {
      splide.refresh()
    },

    /**
     * When the zero index slide has loaded we can remove the original liquid
     * layer. This gives the appearance of the low-res image being faded out and
     * replaced.
     * @param {Number} index
     */
    handleGalleryLoading(index) {
      if (index !== 0) {
        return
      }

      const section = document.querySelector('.main-product__liquid')
      if (!section) {
        return
      }

      section.classList.add('main-product__fade-out')
      setTimeout(() => {
        section.remove()
        this.preloadGalleryImages()
      }, this.$timing('normal'))
    },

    /**
     * Removes the lazy loading attribute on image elements once the Vue has
     * fully loaded and the liquid is removed.
     */
    preloadGalleryImages() {
      const images = this.$el.querySelectorAll('[js-product-gallery="image"] .images__image')

      if (!images.length) {
        return
      }

      images.forEach(image => {
        image.removeAttribute('loading')
      })
    },

    /**
     * Handles the Splide "active" event.
     * - Updates the local user-friendly active slide count.
     *
     * @param {Object} _ - The Splide instance.
     * @param {Object} slide - The event payload.
     */
    handleActiveEvent(_, slide) {
      this.active = slide.isClone ? slide.slideIndex + 1 : slide.index + 1
    },
  },
}
</script>

<style lang="scss">
@import '@splidejs/vue-splide/css/core';
@import './product-gallery';
</style>
